import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, Mail } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { forgotPasswordThunk } from '../../redux/authentication/utils/core/authRecoveryActions';


const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, successMessage, errorMessage } = useSelector((state) => state.authRecovery);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    dispatch(forgotPasswordThunk(email));
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{ background: '#09090f', fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');`}</style>

      {/* Top radial glow — identical to the homepage hero */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 900,
          height: 480,
          background: 'radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.16) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      <button
        onClick={() => navigate('/login')}
        className="absolute top-6 left-6 flex items-center text-violet-300 hover:text-violet-100 transition-colors z-50"
      >
        <ArrowLeft className="w-5 h-5 mr-1" /> {t('auth.back')}
      </button>

      <div className="relative w-full max-w-md z-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">{t('auth.forgotPassword.heading')}</h2>
          <p className="text-gray-400 text-sm">{t('auth.forgotPassword.subtitle')}</p>
        </div>

        <div className="rounded-3xl p-8 shadow-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              <input
                type="email"
                placeholder={t('auth.forgotPassword.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 py-4 bg-white/5 rounded-2xl text-white placeholder-gray-500 border border-white/10 outline-none focus:border-violet-400 transition-colors"
              />
            </div>

            {successMessage && <p className="text-green-400 text-sm">{successMessage}</p>}
            {errorMessage && (
              <div className="p-3 rounded-lg text-sm border bg-red-600/30 border-red-500 text-red-200">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 text-white font-semibold rounded-2xl shadow-lg disabled:opacity-50 transition-all duration-200 active:scale-[0.98]"
              style={{ background: '#7c3aed' }}
            >
              {loading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.submit')}
            </button>
          </form>

          <p className="text-gray-400 text-sm text-center mt-6">
            {t('auth.forgotPassword.rememberPassword')}{' '}
            <Link to="/login" className="text-violet-400 hover:text-violet-300 font-medium transition-colors">{t('auth.forgotPassword.backToLogin')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
